/**
 * PropertyDecorator - metadata decorators for serializable properties.
 */

import 'reflect-metadata';

import type { IDumper, ILoader } from './factories.js';
import type { Constructor } from '../../systems/ClassRegistry.js';
import type { Vector3Like } from 'three';
const constructorMetadataRegistry = new WeakMap<Function, Record<string, PropertyMetadata>>();

// ---------------------------------------------------------------------------
// Generated RPC parameter metadata registry
// ---------------------------------------------------------------------------

const rpcParamMetadataRegistry = new Map<Function, Map<string, NetPropertyMetadata[]>>();

/**
 * Register inferred RPC parameter metadata for a method on a specific constructor.
 * Called from the generated engine-data.ts / game-data.ts files.
 */
export function registerGeneratedRpcMetadata(
  constructor: Function,
  methodName: string,
  params: NetPropertyMetadata[]
): void {
  let methods = rpcParamMetadataRegistry.get(constructor);
  if (!methods) {
    methods = new Map();
    rpcParamMetadataRegistry.set(constructor, methods);
  }
  methods.set(methodName, params);
}

/**
 * Retrieve inferred RPC parameter metadata, walking the prototype chain.
 * Returns `null` if no metadata is registered for the method on this class or any ancestor.
 */
export function getGeneratedRpcMetadata(ctor: Function, methodName: string): NetPropertyMetadata[] | null {
  let current: Function | null = ctor;
  while (current && current !== Function.prototype) {
    const methods = rpcParamMetadataRegistry.get(current);
    if (methods) {
      const params = methods.get(methodName);
      if (params) return params;
    }
    current = Object.getPrototypeOf(current) as Function | null;
  }
  return null;
}

/**
 * Register property metadata for a specific constructor.
 * This is minification-safe as it uses constructor references, not string names.
 * This is used by the generated engine-data.ts and game-data.ts files.
 */
export function registerGeneratedPropertyMetadata(constructor: Function, metadata: Record<string, PropertyMetadata>): void {
  constructorMetadataRegistry.set(constructor, metadata);
}

export interface ClassMetadata {
  /** Human-readable description of the class, mined from the JSDoc comment above @EngineClass/@GameClass. */
  description?: string;
}

const classMetadataRegistry = new WeakMap<Function, ClassMetadata>();

/**
 * Register class-level metadata for a specific constructor.
 * This is minification-safe as it uses constructor references, not string names.
 * This is used by the generated engine-data.ts and game-data.ts files.
 */
export function registerGeneratedClassMetadata(constructor: Function, metadata: ClassMetadata): void {
  classMetadataRegistry.set(constructor, metadata);
}

/**
 * Returns the class-level metadata for a constructor, or undefined if none is registered.
 */
export function getClassMetadata(constructor: Function): ClassMetadata | undefined {
  return classMetadataRegistry.get(constructor);
}

/**
 * Controls which clients receive a replicated property.
 * Used in {@link NetPropertyMetadata.replicateTo}.
 *
 * - `'all'`       — sent to every connected client (default).
 * - `'owner'`     — sent only to the actor's owning client.
 * - `'non-owner'` — sent to all clients except the actor's owning client.
 */
export type ReplicateTo = 'all' | 'owner' | 'non-owner';

/**
 * Quantization settings for `float` and vector component properties.
 * When set, the float value is encoded as an integer (or bit pattern) to reduce bandwidth.
 *
 * - `range` — encodes the value as a fixed-bit unsigned index in `[min, max]`.
 *   Uses exactly `bits` bits per value; suitable when the domain is bounded and
 *   predictable bandwidth cost is needed. `bits` must be 1–32.
 *
 * - `scale` — multiplies by `scale`, rounds to the nearest integer, and encodes
 *   as a zigzag varint. Cost varies with magnitude; best for small values or when
 *   the domain is unbounded. `scale` must be > 0.
 *
 * - `float16` — converts the value to its IEEE 754 half-precision (16-bit) bit
 *   pattern and encodes the delta of that bit pattern as a varsint.
 *
 * - `float32` — converts to a monotonically-ordered 32-bit integer representation
 *   and encodes the delta as a varsint. Small float changes → small deltas.
 *
 * - `float64` — sends the value as an absolute IEEE 754 double-precision (64-bit) float.
 *
 * - `drop-w` — for `quaternion` only. Always drops the `w` component and transmits
 *   x, y, z. The quaternion is normalised and the sign is flipped if necessary so that
 *   w ≥ 0 (hemisphere convention), then w is reconstructed on decode as
 *   `sqrt(max(0, 1 − x² − y² − z²))`. Each transmitted component is quantised with
 *   `bits` bits in `[-1, 1]`, and deltas of the quantized integers are sent as varsints.
 *
 * @example
 * // Position bounded to a ±655m world — 16 bits per axis (~2cm precision)
 * net: { netType: 'vector3', quantization: { mode: 'range', min: -655.35, max: 655.35, bits: 16 } }
 *
 * @example
 * // Health with 1 decimal place — varsint, short for values near 0
 * net: { netType: 'float', quantization: { mode: 'scale', scale: 10 } }
 *
 * @example
 * // Rotation with delta-compressed half-precision bits
 * net: { netType: 'float', quantization: { mode: 'float16' } }
 *
 * @example
 * // Compact unit quaternion using smallest-three encoding (16 bits per component)
 * net: { netType: 'quaternion', quantization: { mode: 'drop-w', bits: 16 } }
 */
export type QuantizationMode =
  | { mode: 'range'; min: number; max: number; bits: number }
  | { mode: 'scale'; scale: number }
  | { mode: 'float16' }
  | { mode: 'float32' }
  | { mode: 'float64' }
  | { mode: 'drop-w'; bits: number };

/**
 * Binary wire types for network replication.
 * Used in {@link NetPropertyMetadata} to describe how a replicated property
 * is encoded and decoded on the wire.
 */
export type ReplicationNetType =
  | 'bool'
  | 'uint'
  | 'int'
  | 'float'
  | 'string'
  | 'actorRef'
  | 'vector2'
  | 'vector3'
  | 'vector4'
  | 'quaternion'
  | 'euler'
  | 'array'
  /**
   * String enum encoded as a bit-packed index into {@link NetPropertyMetadata.enumOptions}.
   * The value is mapped to its index on encode and back to the string on decode.
   */
  | 'enum'
  /**
   * Fixed-type plain data struct (not in ClassRegistry).
   * Constructor is provided via {@link NetPropertyMetadata.structClass}.
   */
  | 'struct'
  /**
   * Registered engine/game class (in ClassRegistry).
   * Base class name provided via {@link NetPropertyMetadata.netBaseClass}.
   * Set {@link NetPropertyMetadata.polymorphic} to enable class discriminator.
   */
  | 'class';

/**
 * Binary serialization metadata for a replicated property.
 * Nested inside {@link PropertyMetadataBase.net}.
 *
 * @example
 * ```ts
 * // Simple float (absolute float32 wire format)
 * net: { netType: 'float' }
 *
 * // Nullable actor reference
 * net: { netType: 'actorRef', nullable: true }
 *
 * // Array of float elements
 * net: { netType: 'array', element: { netType: 'float' } }
 *
 * // Nested struct (fixed type)
 * net: { netType: 'struct', structClass: ActorNetTransform }
 *
 * // Polymorphic registered class
 * net: { netType: 'class', netBaseClass: 'ENGINE.MyBase', polymorphic: true }
 *
 * // String enum (index-mapped to uint8 on the wire)
 * net: { netType: 'enum', enumOptions: ['None', 'Authority', 'AutonomousProxy', 'SimulatedProxy'] }
 * ```
 */
export interface NetPropertyMetadata {
  /** Wire encoding type. Required. */
  netType: ReplicationNetType;

  /**
   * Whether the value can be null on the wire.
   * When true, a 1-byte presence flag is written before the value.
   * When false (default), the value is always present.
   */
  nullable?: boolean;

  /**
   * Constructor for `struct`-typed properties.
   * The constructor must accept zero arguments.
   * Always overwritten by the property metadata generator; any user-provided value is ignored.
   */
  structClass?: Function;

  /**
   * Full registered class name (e.g. `'ENGINE.MyBase'`) for `class`-typed properties.
   * Used with {@link ClassRegistry.getClass} for construction.
   * Inferred automatically from `@EngineClass` / `@AbstractEngineClass` / `@GameClass`
   * when the property type is a registered class; can be overridden if needed.
   */
  netBaseClass?: string;

  /**
   * When true (and `netType === 'class'`), a uint32 FNV-1a class discriminator
   * is written before the nested fields, enabling polymorphic deserialization.
   * Defaults to `true` when auto-inferred for a registered class type; set to `false`
   * to opt out of the class discriminator overhead for known-monomorphic properties.
   */
  polymorphic?: boolean;

  /**
   * Ordered list of string values for `enum`-typed properties.
   * The array index is the wire value (uint8); the string is the runtime enum value.
   * Always overwritten by the property metadata generator; any user-provided value is ignored.
   */
  enumOptions?: string[];

  /**
   * Element metadata for `array`-typed properties.
   * Supports nesting (array of arrays) by recursion.
   */
  element?: NetPropertyMetadata;

  /**
   * Controls which clients receive this replicated property.
   * Defaults to `'all'` when generated automatically by the property metadata generator.
   *
   * - `'all'`       — sent to every connected client.
   * - `'owner'`     — sent only to the actor's owning client.
   * - `'non-owner'` — sent to all clients except the actor's owning client.
   */
  replicateTo?: ReplicateTo;

  /**
   * Per-component encoding type for `vector2`, `vector3`, `vector4`, `quaternion`, and `euler`
   * properties. Defaults to `float32` absolute when not specified.
   *
   * Use integer types for semantically integer vectors (e.g. `'uint'` for RGBA color components,
   * `'int'` for grid-space coordinates).
   *
   * @example
   * // World-grid offset stored as integer vector3
   * net: { netType: 'vector3', vectorElementType: 'int' }
   */
  vectorElementType?: 'uint' | 'int';

  /**
   * Quantization settings for `float` and vector component properties.
   * When set, the float value is encoded as an integer to reduce bandwidth.
   * Mutually exclusive with {@link vectorElementType} — quantization takes precedence
   * for float net types. See {@link QuantizationMode} for details.
   */
  quantization?: QuantizationMode;

  /**
   * Minimum change threshold (deadband) for delta-encoded properties.
   * When set, a property update is suppressed if the absolute change in the
   * serialized representation is ≤ `epsilon`.
   *
   * For `float` without a quantization mode and for unquantized vector components,
   * the threshold is applied in float space. For quantized scalars and float16/32
   * modes it is applied in the serialized integer space. Defaults to `0` (every
   * change is sent).
   */
  epsilon?: number;
}

export interface PropertyMetadataBase {
  readonly?: boolean;
  hidden?: boolean;
  description?: string;
  category?: string;
  order?: number;
  categoryOrder?: number;

  /**
   * Whether this property must have a non-null, non-empty value for the component to be
   * considered properly configured. When true, `SceneComponent.getConfigurationWarnings()`
   * will automatically report a warning if the value is null, undefined, or an empty string.
   */
  required?: boolean;

  // ----------------------------------
  // Network replication options
  // ----------------------------------
  /**
   * Whether this property should be replicated over the network.
   * If true, the property will be included in network updates via {@link ReplicationSerializer}.
   */
  replicate?: boolean;

  /**
   * Binary serialization metadata for this property when `replicate` is true.
   * Describes the wire encoding type and options (nullable, struct class, etc.).
   * Populated automatically by the property metadata generator. Partial overrides
   * are supported on `@property()` decorators — the generator fills missing fields
   * and always overwrites `structClass` and `enumOptions`.
   */
  net?: NetPropertyMetadata;

  // ----------------------------------\
  // Serialization options
  // ----------------------------------
  /**
   * the default value of this property, it is used to skip serialization if the value is the same as the default
   * Since the serializer uses strict equality (===) to compare values, only primitive types are supported here.
   */
  default?: string | number | boolean | null | undefined;

  /**
   * whether this property should be skipped during serialization.
   * If true, the property will not be serialized whatsoever.
   */
  transient?: boolean;

  /**
   * whether this property should be skipped when dumped as a prefab.
   * If true, the property will not be dumped when part of a prefab, but it will be dumped normally otherwise.
   * This is useful for properties that should not be shared across prefab instances, e.g. uuid, name
   */
  transientOnPrefab?: boolean;

  /**
   * Force serialization of this property even when its value equals the base/prefab default,
   * as long as the value is "set" (not `undefined`, `null`, or an empty string).
   *
   * Normally the dumper omits a property whose value matches the default coming from the
   * class CDO or prefab template. Stable-identity fields (e.g. {@link SceneComponent.prefabNodeId})
   * must persist on prefab instances even though they equal the template value, so the loader
   * can match instance children back to the current template by identity. Empty values are still
   * omitted so non-prefab objects don't gain noise.
   */
  alwaysSerializeWhenSet?: boolean;

  /**
   * whether this property should be skipped when loaded if it's not in editor environment.
   * Useful for properties that are only relevant in editor, e.g. editor-only metadata.
   */
  editorOnly?: boolean;

  /**
   * the property name to migrate from when loading old serialized data.
   * useful for renaming properties while maintaining backward compatibility.
   *
   * @example
   * ```ts
   * @property({ migrateFrom: 'oldPropertyName' })
   * newPropertyName: string;
   * ```
   */
  migrateFrom?: string;

  /**
   * Custom serialization function for this property.
   *
   * @param value - The property value
   * @param dumper - The dumper instance
   */
  serialize?: (value: any, dumper: IDumper) => void;

  /**
   * Custom deserialization function for this property.
   *
   * @param data - The serialized data
   * @returns The deserialized property value
   */
  deserialize?: (data: any, loader: ILoader) => any;

  /**
   * Custom array element resolver for loading array properties.
   * When loading an array, this function is called for each element to find the
   * matching existing element in the target array. This enables proper handling
   * of reordered array elements or elements matched by custom criteria (e.g., name).
   *
   * @param data - The dereferenced object data for this array element. If the serialized
   *   value was a `$ref`, this is the resolved `$value` content. Otherwise it's the
   *   serialized element directly.
   * @param existingArray - The existing target array being loaded into
   * @param index - The index position in the serialized array
   * @returns The existing element to populate, or undefined to use index-based matching
   *
   * @example
   * ```ts
   * @property({
   *   resolveArrayElement: (data, existing, index) => {
   *     // Match by name if available, fallback to index-based
   *     const match = existing.find(e => e.name === data?.name);
   *     return match ?? existing[index];
   *   }
   * })
   * children: SceneComponent[] = [];
   * ```
   */
  resolveArrayElement?: (
    data: any,
    existingArray: any[],
    index: number
  ) => any | undefined;

  /**
   * Custom selector property name to populate options for enum/multiselect types.
   * If a string, it will be treated as a method name on the target, parent target, or root target.
   */
  selector?: string;
}

export const PROPERTY_METADATA_KEY = Symbol('propertyMetadata');

export function getPropertyMetadata(target: any): Record<string, PropertyMetadata> {
  const metadata: Record<string, PropertyMetadata> = {};

  // Walk the prototype chain to collect all inherited metadata
  let current = target;
  while (current && current !== Object.prototype) {
    // Check for generated metadata from registry (minification-safe via constructor reference)
    const ctor = current.constructor;
    if (ctor && constructorMetadataRegistry.has(ctor)) {
      const generatedMeta = constructorMetadataRegistry.get(ctor)!;
      // Merge: parent metadata comes first, child can override
      for (const [key, value] of Object.entries(generatedMeta)) {
        if (!metadata[key]) {
          metadata[key] = value as PropertyMetadata;
        } else if (typeof value === 'object' && value !== null) {
          // Merge: child metadata overrides parent, but fill in missing fields
          metadata[key] = { ...value, ...metadata[key] } as PropertyMetadata;
        }
      }
    }

    // Also check for Reflect metadata from @property decorators
    const reflectMeta = Reflect.getOwnMetadata(PROPERTY_METADATA_KEY, current);
    if (reflectMeta) {
      for (const [key, value] of Object.entries(reflectMeta)) {
        if (!metadata[key]) {
          metadata[key] = value as PropertyMetadata;
        } else if (typeof value === 'object' && value !== null) {
          // Merge: decorator metadata overrides generated, but fill in missing fields
          metadata[key] = { ...metadata[key], ...value } as PropertyMetadata;
        }
      }
    }

    current = Object.getPrototypeOf(current);
  }

  return metadata;
}

export interface PropertyMetadataPerClassEntry {
  constructor: Function;
  /** Registered class name, or constructor.name as fallback */
  className: string;
  /** Only the properties declared by this specific class (not inherited) */
  properties: Record<string, PropertyMetadata>;
}

/**
 * Returns property metadata grouped by the class that declares each property, in order
 * from most-derived (child) class to least-derived (ancestor) class.
 *
 * Unlike `getPropertyMetadata` which merges the entire chain into one flat record, each
 * entry here contains only the properties registered directly on that class via
 * `registerGeneratedPropertyMetadata` (populated by engine-data.ts / game-data.ts).
 * Classes not present in that registry (e.g. THREE.Object3D and other non-engine base
 * classes) are skipped — their properties are already surfaced through SceneComponent.
 *
 * An optional `resolveConstructor` callback can be supplied so callers (e.g. the editor)
 * can substitute a fresh constructor reference when modules have been reloaded (mirrors
 * the ClassRegistry lookup done by `getUnifiedPropertyMetadata`).
 *
 * @param target - An object instance, or any prototype in the chain.
 * @param resolveConstructor - Optional: given a constructor, return the canonical
 *   (fresh) version of it. Return `undefined` to keep the original.
 */
export function getPropertyMetadataPerClass(
  target: any,
  resolveConstructor?: (ctor: Function) => Function | undefined
): PropertyMetadataPerClassEntry[] {
  const result: PropertyMetadataPerClassEntry[] = [];

  // Start from the prototype if given an instance, or use the value directly as a prototype.
  let current: any = typeof target === 'object' && target !== null
    ? Object.getPrototypeOf(target)
    : target;

  while (current && current !== Object.prototype) {
    const rawCtor: Function | undefined = current.constructor;
    const ctor = rawCtor && resolveConstructor ? (resolveConstructor(rawCtor) ?? rawCtor) : rawCtor;

    if (ctor && constructorMetadataRegistry.has(ctor)) {
      const ownMeta = constructorMetadataRegistry.get(ctor)!;
      const className = (ctor as any)?.name ?? '';
      result.push({ constructor: ctor, className, properties: { ...ownMeta } });
    }

    current = Object.getPrototypeOf(current);
  }

  return result;
}

export function hasPropertyMetadata(target: any): boolean {
  // Walk the prototype chain to check if any metadata exists
  let current = target;
  while (current && current !== Object.prototype) {
    const ctor = current.constructor;
    if (ctor && constructorMetadataRegistry.has(ctor)) {
      return true;
    }
    current = Object.getPrototypeOf(current);
  }
  return false;
}

export function shouldShowProperty(metadata: PropertyMetadata): boolean {
  return !metadata.hidden;
}

/**
 * Get all properties marked with replicate=true from a target object
 * Efficiently returns only replicated properties for network serialization
 * @param target The object instance to get replicated properties from
 * @returns Map of property names to their metadata for properties with replicate=true
 */
export function getReplicatedProperties(target: any): Map<string, PropertyMetadata> {
  const allProperties = getPropertyMetadata(target);
  const replicatedProps = new Map<string, PropertyMetadata>();

  for (const [key, metadata] of Object.entries(allProperties)) {
    if (metadata?.replicate === true) {
      replicatedProps.set(key, metadata);
    }
  }

  return replicatedProps;
}

// TypeScript type aliases for path types
export type AudioPath = string;
export type HdriPath = string;
export type JsclassPath = string;
export type JsonPath = string;
export type MaterialPath = string;
export type ModelPath = string;
export type PrefabPath = string;
export type ResourcePath = string;
export type ScenePath = string;
export type SourcecodePath = string;
export type TexturePath = string;
export type VfxPath = string;
export type VideoPath = string;
export type VoxelPath = string;

export const RESOURCE_PATH_TYPES = [
  'audioPath',
  'hdriPath',
  'jsclassPath',
  'jsonPath',
  'materialPath',
  'modelPath',
  'prefabPath',
  'resource',
  'scenePath',
  'sourcecodePath',
  'texturePath',
  'vfxPath',
  'videoPath',
  'voxelPath',
] as const;

export const PROPERTY_TYPES = [
  'actorReference',
  'array',
  'boolean',
  'bufferGeometry',
  'class',
  'color',
  'componentReference',
  'enum',
  'euler',
  'multiselect',
  'quaternion',
  'number',
  'object',
  'spawnable',
  'string',
  'texture',
  'unknown',
  'vector2',
  'vector3',
  'vector4',
  ...RESOURCE_PATH_TYPES
] as const;

export type PropertyType = typeof PROPERTY_TYPES[number];

export function isValidPropertyType(type: string): type is PropertyType {
  return (PROPERTY_TYPES as readonly string[]).includes(type);
}

export type PropertyMetadata = PropertyMetadataBase & (
  | { type?: 'actorReference'; actorClass?: string }
  | { type?: 'array'; minLength?: number; maxLength?: number; child?: PropertyMetadata }
  | { type?: 'audioPath' }
  | { type?: 'boolean' }

  | { type?: 'bufferGeometry' }
  | { type?: 'class'; classType?: Constructor; componentClass?: string }
  | { type?: 'color' }
  | { type?: 'componentReference'; componentClass?: string }
  | { type?: 'enum'; options?: string[] | Record<string, string> }
  | { type?: 'euler'; min?: number; max?: number; step?: number; decimals?: number }
  | { type?: 'quaternion' }
  | { type?: 'hdriPath' }
  | { type?: 'jsclassPath' }
  | { type?: 'jsonPath' }
  | { type?: 'materialPath' }
  | { type?: 'modelPath' }
  | {
    /** Multi-select allows selecting multiple values from a list of options */
    type?: 'multiselect';
    /** Available options for selection */
    options?: string[] | Record<string, string>;
    /** Placeholder text when no selection is made */
    placeholder?: string;
    /** Maximum number of selections allowed (default: unlimited) */
    maxSelected?: number;
    /** Allow creating new values not in options list (default: false) */
    createNewAllowed?: boolean;
    /** Auto-format labels to display case (default: true) */
    useDisplayCase?: boolean;
    /** Child type for arrays */
    child?: PropertyMetadata;
  }
  | { type?: 'number'; min?: number; max?: number; step?: number; decimals?: number; unit?: string }
  | { type?: 'object'; fields?: Record<string, PropertyMetadata> }
  | { type?: 'prefabPath' }
  | { type?: 'resource', resourceType?: string }
  | { type?: 'scenePath' }
  | { type?: 'sourcecodePath' }
  | { type?: 'spawnable'; spawnableClass?: string }
  | { type?: 'string'; minLength?: number; maxLength?: number; pattern?: string }
  | { type?: 'texture' }
  | { type?: 'texturePath' }
  | { type?: 'unknown' }
  | { type?: 'vector2'; showLinking?: boolean; min?: number; max?: number; step?: number; decimals?: number }
  | { type?: 'vector3'; showLinking?: boolean; min?: number; max?: number; step?: number; decimals?: number; initialValue?: Vector3Like; }
  | { type?: 'vector4'; showLinking?: boolean; min?: number; max?: number; step?: number; decimals?: number }
  | { type?: 'vfxPath' }
  | { type?: 'videoPath' }
  | { type?: 'voxelPath' }
);

/**
 * Type for class-level property metadata mapping
 * Maps property names to their metadata definitions
 */
export type ClassPropertyMetadata = Record<string, PropertyMetadata>;

/**
 * Returns a default value for a property based on its metadata type.
 * Returns undefined if the type is unknown or cannot determine a default.
 */
export function getDefaultValueForType(meta: PropertyMetadata): unknown {
  switch (meta.type) {
    case 'string':
    case 'modelPath':
    case 'materialPath':
    case 'texturePath':
    case 'hdriPath':
    case 'audioPath':
    case 'videoPath':
    case 'scenePath':
    case 'prefabPath':
    case 'jsonPath':
    case 'sourcecodePath':
    case 'jsclassPath':
    case 'resource':
    case 'vfxPath':
    case 'voxelPath':
    case 'spawnable':
      return '';
    case 'texture':
      return null;
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'color':
      return '#ffffff';
    case 'enum':
      if ('options' in meta && meta.options) {
        const options = meta.options;
        if (Array.isArray(options) && options.length > 0) {
          return options[0];
        }
        if (typeof options === 'object') {
          const keys = Object.keys(options);
          if (keys.length > 0) return keys[0];
        }
      }
      return undefined;
    case 'euler':
      return { x: 0, y: 0, z: 0 };
    case 'vector2':
      return { x: 0, y: 0 };
    case 'vector3':
      if ('initialValue' in meta && meta.initialValue !== undefined && meta.initialValue !== null) {
        return { x: meta.initialValue.x, y: meta.initialValue.y, z: meta.initialValue.z };
      }
      return { x: 0, y: 0, z: 0 };
    case 'quaternion':
    case 'vector4':
      return { x: 0, y: 0, z: 0, w: 1 };
    case 'actorReference':
    case 'componentReference':
    case 'bufferGeometry':
      return null;
    case 'multiselect':
      return [];
    case 'object':
      if ('fields' in meta && meta.fields) {
        const obj: Record<string, unknown> = {};
        for (const [fieldName, fieldMeta] of Object.entries(meta.fields)) {
          obj[fieldName] = getDefaultValueForType(fieldMeta);
        }
        return obj;
      }
      return {};
    default:
      return undefined;
  }
}

/**
 * Property decorator that marks class properties as serializable.
 * Cannot be used on method properties - use @rpc or other appropriate decorators for methods.
 *
 * This decorator uses a type constraint that produces a compile-time error
 * when applied to function-valued properties (methods).
 *
 * @param meta - Optional metadata configuration for the property
 * @returns A decorator function
 *
 * @example
 * ```ts
 * class MyComponent {
 *   @property() myValue: number = 0;  // OK
 *
 *   // @property() myMethod() {}  // Compile error: method not allowed
 * }
 * ```
 */
export function property<T extends object, K extends string | symbol>(
  meta?: PropertyMetadata
): (target: T, key: K extends keyof T ? (T[K] extends (...args: any[]) => any ? never : K) : K) => void {
  return ((target: T, key: K) => {
    const parent = Object.getPrototypeOf(target);
    const parentMeta = Reflect.getMetadata(PROPERTY_METADATA_KEY, parent) ?? {};
    const existing = { ...parentMeta, ...Reflect.getOwnMetadata(PROPERTY_METADATA_KEY, target) };
    existing[key as string] = meta ?? {};
    Reflect.defineMetadata(PROPERTY_METADATA_KEY, existing, target);
  }) as (target: T, key: K) => void;
}

// ====================================================================================================
// Callable Method Metadata
// ====================================================================================================

export const CALLABLE_METADATA_KEY = Symbol('callableMetadata');

/**
 * Metadata for methods that can be called from the editor.
 * Used by the @callable decorator to expose methods as buttons in the property panel.
 */
export interface CallableMetadata {
  /** Custom label for the button. If not provided, the method name is used. */
  label?: string;
  /** Tooltip text shown when hovering over the button */
  tooltip?: string;
  /** Category for grouping buttons in the property panel */
  category?: string;
  /** Display order within the category (lower values appear first) */
  order?: number;
  /** Category display order (lower values appear first) */
  categoryOrder?: number;
}

/**
 * Returns all callable method metadata for a target object.
 * Walks the prototype chain to collect metadata from all parent classes.
 *
 * @param target - The object instance or prototype to get callable metadata for
 * @returns Record mapping method names to their CallableMetadata
 */
export function getCallableMetadata(target: any): Record<string, CallableMetadata> {
  const metadata: Record<string, CallableMetadata> = {};

  // Walk the prototype chain to collect all inherited metadata
  let current = target;
  while (current && current !== Object.prototype) {
    // Check for Reflect metadata from @callable decorators
    const reflectMeta = Reflect.getOwnMetadata(CALLABLE_METADATA_KEY, current);
    if (reflectMeta) {
      for (const [key, value] of Object.entries(reflectMeta)) {
        if (!metadata[key]) {
          metadata[key] = value as CallableMetadata;
        }
      }
    }

    current = Object.getPrototypeOf(current);
  }

  return metadata;
}

/**
 * Decorator that marks a method as callable from the editor.
 * Adds a button to the property panel that invokes this method when clicked.
 *
 * @param meta - Optional metadata configuration for the callable method
 * @returns A decorator function
 *
 * @example
 * ```ts
 * class MyComponent extends SceneComponent {
 *   @callable({ label: 'Refresh Data', tooltip: 'Reloads all cached data', category: 'Actions' })
 *   refreshData(): void {
 *     // Method implementation
 *   }
 * }
 * ```
 */
export function callable(
  meta?: CallableMetadata
): (target: object, key: string | symbol, descriptor: PropertyDescriptor) => void {
  return (target: object, key: string | symbol, _descriptor: PropertyDescriptor) => {
    const existing = Reflect.getOwnMetadata(CALLABLE_METADATA_KEY, target) ?? {};
    existing[key as string] = meta ?? {};
    Reflect.defineMetadata(CALLABLE_METADATA_KEY, existing, target);
  };
}
